import {useEffect, useCallback, Fragment, useMemo, useRef} from 'react'
import {
  startOfWeek,
  addDays,
  format,
  eachMinuteOfInterval,
  isSameDay,
  isBefore,
  isToday,
  setMinutes,
  setHours,
  isWithinInterval,
  isAfter,
  endOfDay,
  startOfDay,
  addMinutes,
  isEqual,
} from 'date-fns'
import TodayTypo from '../common/TodayTypo'
import EventItem from '../events/EventItem'
import {CellRenderedProps, DayHours, DefaultRecourse, ProcessedEvent} from '../../types'
import {
  calcCellHeight,
  calcMinuteHeight,
  comparePositions,
  differenceInDaysOmitTime,
  filterMultiDaySlot,
  filterTodayEvents,
  filterTodayEventsForResource,
  getHoursOfOperationOnDay,
  getOverTimeHours,
  getResourcedEvents,
  getTimeZonedDate,
  isUnassignedEventRowId,
  WeekDays,
} from '../../helpers/generals'
import {dayAvailabilitySummary, MINUTES_IN_DAY} from '../../helpers/availability'
import {AssignEmployeeSelect} from '../common/AssignEmployeeSelect'
import {WithResources} from '../common/WithResources'
import Cell from '../common/Cell'
import TodayEvents from '../events/TodayEvents'
import {TableGrid, TableGridTimeline} from '../../styles/styles'
import {MULTI_DAY_EVENT_HEIGHT} from '../../helpers/constants'
import {useStore} from '../../store'
import useSyncScroll from '../../hooks/useSyncScroll'
import {useState} from 'react'
import {alpha, Avatar, useTheme} from '@mui/material'
import {Nav, Tab} from 'react-bootstrap'
import {toAbsoluteUrl} from '../../../../../_fds/helpers'
import {ResourceLabel} from '../resource/ResourceLabel'
import {useRowHeights} from '../resource/useRowHeights'

export interface EmployeesWeekProps {
  weekDays: WeekDays[]
  weekStartOn: WeekDays
  startHour: DayHours
  endHour: DayHours
  step: number
  cellRenderer?(props: CellRenderedProps): JSX.Element
  navigation?: boolean
  disableGoToDay?: boolean
}

const EmployeesWeek = () => {
  const {
    week,
    selectedDate,
    height,
    events,
    handleGotoDay,
    handleState,
    resources,
    resourceFields,
    fields,
    direction,
    locale,
    hourFormat,
    hoursOfOperations,
    draggable,
    timeZone,
    employeeHours,
    selectedPosition,
    employeeAvailability,
  } = useStore()

  const {weekStartOn, weekDays, startHour, endHour, step, cellRenderer, disableGoToDay} = week!
  const _weekStart = startOfWeek(selectedDate, {weekStartsOn: weekStartOn})
  const daysList = weekDays.map((d) => addDays(_weekStart, d))

  // use integer part of startHour for hour and decimal part for minutes
  // get decimal part of startHour
  const startHourDecimal = startHour - Math.floor(startHour)
  const START_TIME = setMinutes(setHours(selectedDate, startHour), startHourDecimal * 60)
  // use integer part of endHour for hour and decimal part for minutes
  // get decimal part of endHour
  const endHourDecimal = endHour - Math.floor(endHour)
  const END_TIME = addMinutes(setMinutes(setHours(selectedDate, endHour), endHourDecimal * 60), -30)
  // if END_TIME is before START_TIME, it means that the day is over midnight
  // so we need to add 24 hours to the end time
  if (isBefore(END_TIME, START_TIME) || isEqual(END_TIME, START_TIME)) {
    END_TIME.setHours(END_TIME.getHours() + 24)
  }

  const hours = eachMinuteOfInterval(
    {
      start: START_TIME,
      end: END_TIME,
    },
    {step: step}
  )
  const CELL_HEIGHT = calcCellHeight(height, hours.length)
  // A row grows past CELL_HEIGHT only when its name label had to wrap.
  const {heightOf, reporterFor} = useRowHeights(CELL_HEIGHT)
  const MINUTE_HEIGHT = calcMinuteHeight(CELL_HEIGHT, step)
  const MULTI_SPACE = 0 //MULTI_DAY_EVENT_HEIGHT
  const hFormat = hourFormat === '12' ? 'hh:mm a' : 'HH:mm'
  const {headersRef, bodyRef} = useSyncScroll()

  const renderTable = (resource?: DefaultRecourse) => {
    // console.log("RENDERING EMPLOYEES WEEK'S TABLE WITH RESOURCES", resources)
    // Unassigned-shift rows stay pinned on top (shift-start order from
    // withUnassignedShiftRows); employees sort by hours/position below them.
    const unassignedRows = resources.filter((r) => isUnassignedEventRowId(r.employee_id))
    const employeeRows = resources.filter((r) => !isUnassignedEventRowId(r.employee_id))
    const sortedResources = [
      ...unassignedRows,
      ...employeeRows.sort((a, b) => {
        const employeeIdA = a.employee_id
        const employeeIdB = b.employee_id
        const occurrenceA = employeeHours.get(employeeIdA) || 0
        const occurrenceB = employeeHours.get(employeeIdB) || 0

        if (occurrenceA === occurrenceB) {
          // Position order comes from the data-driven registry (sort_order).
          return (
            comparePositions(a.position, b.position) ||
            (a.first_name || '').localeCompare(b.first_name || '') ||
            (a.last_name || '').localeCompare(b.last_name || '')
          )
        }
        return occurrenceB - occurrenceA
      }),
    ]
    const headerHeight = MULTI_SPACE + 45
    const otHours = getOverTimeHours(events)
    // get sum of values in object
    const totalOtHours = Object.values(otHours).reduce((a, b) => a + b, 0)
    // get count of the keys in object
    const totalOtEmp = Object.keys(otHours).length
    return (
      <>
        {/* Header days */}
        <TableGridTimeline hours={daysList.length} ref={headersRef} sticky='1'>
          {totalOtHours ? (
            <span className='rs__cell rs__time bg-light text-gray-900'>
              OT: {new Intl.NumberFormat('en-US', {maximumFractionDigits: 1}).format(totalOtHours)}{' '}
              hrs <br />
              OT Emp: {totalOtEmp}
            </span>
          ) : (
            <span className='rs__cell rs__time bg-light text-gray-900'></span>
          )}

          {daysList.map((date, i) => (
            <span
              key={i}
              className={`rs__cell rs__header rs__time bg-light text-gray-900 ${
                isToday(date) ? 'rs__today_cell' : ''
              }`}
              style={{height: headerHeight}}
            >
              <TodayTypo
                date={date}
                onClick={!disableGoToDay ? handleGotoDay : undefined}
                locale={locale}
              />
              {/* {renderMultiDayEvents(recousedEvents, date)} */}
            </span>
          ))}
        </TableGridTimeline>
        {/* Time Cells */}
        <TableGridTimeline hours={daysList.length} ref={bodyRef}>
          {sortedResources.map((res, i) => {
            const text = res[resourceFields.textField]
            const subtext = res[resourceFields.subTextField || '']
            const avatar = res[resourceFields.avatarField || '']
            const color = res[resourceFields.colorField || '']
            const positionField = 'position'
            const rowHeight = heightOf(res[resourceFields.idField])
            return (
              // Key by resource identity, not index: when an unassigned row is
              // assigned and removed, index keys make React reuse the instance
              // for the NEXT row — leaking the picker's selected-employee state.
              <Fragment key={String(res[resourceFields.idField] ?? i)}>
                <span
                  style={{height: rowHeight}}
                  className='rs__cell rs__header rs__resource bg-light'
                >
                  <span
                    className='fs-7 m-0 text-gray-900 rs__hover__op'
                    style={{
                      display: 'flex',
                      flexDirection: 'row',
                      alignItems: 'center',
                      width: '100%',
                    }}
                  >
                    {isUnassignedEventRowId(res[resourceFields.idField]) ? (
                      // Unassigned-shift row: the leading cell IS the picker.
                      // Stretch over (most of) the cell's 20px left padding so
                      // the 200px column is used to the fullest.
                      <span
                        className='fs-7 m-0 text-gray-900'
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          width: 'calc(100% + 14px)',
                          marginLeft: '-14px',
                          paddingRight: '4px',
                        }}
                      >
                        <ResourceLabel
                          fitPrimary={false}
                          primary={(() => {
                            const unassignedEvent = events.find(
                              (ev) => ev.employee_id === res[resourceFields.idField]
                            )
                            return unassignedEvent ? (
                              <AssignEmployeeSelect event={unassignedEvent} />
                            ) : null
                          })()}
                          secondary={(subtext || '').toUpperCase()}
                          secondaryColor={color}
                          onNeededHeight={reporterFor(res[resourceFields.idField])}
                        />
                      </span>
                    ) : (
                      <ResourceLabel
                        primary={
                          text.toUpperCase() +
                          (employeeHours.get(res[resourceFields.idField])
                            ? ` (` +
                              new Intl.NumberFormat('en-US', {maximumFractionDigits: 1}).format(
                                employeeHours.get(res[resourceFields.idField]) || 0
                              ) +
                              `h)`
                            : '')
                        }
                        secondary={subtext.toUpperCase()}
                        secondaryColor={color}
                        primaryCursor='pointer'
                        onPrimaryClick={() => {
                          handleState('week', 'view')
                          handleState(res[resourceFields.idField], 'selectedResource')
                        }}
                        onNeededHeight={reporterFor(res[resourceFields.idField])}
                      />
                    )}
                  </span>
                </span>
                {daysList.map((date, ii) => {
                  const [start, end] = getHoursOfOperationOnDay(date, hoursOfOperations)

                  const field = resourceFields.idField
                  const positionField = 'position'
                  let recousedEvents = events
                  if (res) {
                    recousedEvents = getResourcedEvents(events, res, resourceFields, fields)
                  }
                  const daysEvents = filterTodayEventsForResource(
                    recousedEvents,
                    date,
                    res,
                    resourceFields,
                    fields,
                    timeZone
                  )
                  // A cell here is a whole day, so the day collapses to its
                  // most restrictive availability over the open hours. An
                  // overnight close runs past midnight, where the next ISO day
                  // owns the availability — so clamp rather than wrap.
                  const availability = dayAvailabilitySummary(
                    employeeAvailability,
                    res ? res[field] : null,
                    date,
                    start.getHours() * 60 + start.getMinutes(),
                    isSameDay(start, end) ? end.getHours() * 60 + end.getMinutes() : MINUTES_IN_DAY
                  )
                  return (
                    <div
                      key={ii}
                      className={`rs__cell rs__header rs__time ${
                        isToday(date) ? 'rs__today_cell' : ''
                      }`}
                    >
                      {/* Events of each day - run once on the top hour column */}
                      {/* {daysEvents[0] && (
                        <TodayEvents
                          todayEvents={filterTodayEvents(recousedEvents, date, timeZone)}
                          today={date}
                          minuteHeight={MINUTE_HEIGHT}
                          startHour={startHour}
                          step={step}
                          direction={direction}
                          view='timeline'
                        />
                      )} */}
                      <Cell
                        start={start}
                        end={addMinutes(start, step)}
                        day={date}
                        hoursOfOperations={hoursOfOperations}
                        availabilityCode={availability.code}
                        availabilityTitle={availability.title}
                        draggable={draggable || true}
                        height={rowHeight}
                        resourceKey={field}
                        resourceVal={res ? res[field] : null}
                        positionKey={positionField}
                        positionVal={res ? res[positionField] : null}
                        cellRenderer={cellRenderer}
                        children={
                          <TodayEvents
                            todayEvents={daysEvents}
                            today={date}
                            minuteHeight={MINUTE_HEIGHT}
                            startHour={startHour}
                            step={step}
                            direction={direction}
                            view='employeesWeek'
                            handlePlusButton={(e: any) => {
                              e.preventDefault()
                              e.stopPropagation()
                              handleState('week', 'view')
                              handleState(res[resourceFields.idField], 'selectedResource')
                            }}
                          />
                        }
                      />
                    </div>
                  )
                })}
              </Fragment>
            )
          })}
        </TableGridTimeline>
      </>
    )
  }
  const tableContent = useMemo(() => {
    // console.log('RENDERING EMPLOYEES WEEK')
    return renderTable()
    // Every input renderTable actually reads. selectedDate and events were
    // missing, so moving to another week redrew nothing unless something else
    // in this list happened to change too — and employeeHours, the one that
    // usually did, is compared by VALUE in the store (areMapsEqual), so two
    // weeks with the same hours per person left last week's shifts on screen.
    // hoursOfOperations and week drive the column headers and the day bounds.
    //
    // Availability arrives after the first render (one request per location
    // and visible week), so the memo has to see it or the board never picks
    // the shading up.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    resources,
    selectedPosition,
    employeeHours,
    employeeAvailability,
    events,
    selectedDate,
    hoursOfOperations,
    week,
  ])

  return tableContent
}

export {EmployeesWeek}
